import {
  Body,
  Button,
  CustomLink,
  Dropdown,
  Flex,
  LazyImage,
  Shape,
  styled,
  SubTitle,
} from '@teko/ds-desktop';
import { ProductPrice } from '@teko/teko-ecommerce';
import { getTrackingData, TrackingObject } from '@teko/tracking';
import { useTenantConfig } from 'contexts';
import { t } from 'i18n';
import { IActiveCombo, IComboPromotion, IConditionProduct } from 'types';
import { PromotionBenefitInfo } from './PromotionBenefitInfo';

const MAX_CONDITION_ITEMS = 3;

interface ComboItemProps {
  combo: IActiveCombo;
  onClickProductChange: (selectedSku: string, conditionKey: number) => void;
}

interface ConditionItemProps {
  conditionKey: number;
  requiredQuantity: number;
  product: IConditionProduct;
  tracking: TrackingObject;
  conditionPromotion?: IComboPromotion;
  onClickProductChange: (selectedSku: string, conditionKey: number) => void;
}

interface CompactedConditionItemProps {
  residualProducts: Array<ResidualProducts>;
  onClickProductChange: (selectedSku: string, conditionKey: number) => void;
}

interface ResidualProducts {
  key: number;
  requiredQuantity: number;
  product: IConditionProduct;
  tracking: TrackingObject;
}

export const ComboItem: React.FC<ComboItemProps> = (props) => {
  const { combo, onClickProductChange } = props;
  const { totalProduct, promotion, conditions } = combo;

  const displayConditionItems =
    conditions.length > MAX_CONDITION_ITEMS
      ? conditions.slice(0, MAX_CONDITION_ITEMS - 1)
      : conditions;

  const residualProducts =
    conditions.length > MAX_CONDITION_ITEMS
      ? conditions
          .slice(MAX_CONDITION_ITEMS - 1)
          .map((residualProduct, index) => ({
            ...residualProduct,
            tracking: getTrackingData({
              isTrackContent: true,
              regionName: 'productComboList',
              contentName: 'btnChangeProductCombo',
              payload: JSON.stringify({
                comboTab: `Combo ${totalProduct} sản phẩm`,
                promotionType: 'combo',
                skuIndex: MAX_CONDITION_ITEMS + index,
                sku: residualProduct.product.sku,
              }),
            }),
          }))
      : [];

  return (
    <>
      <Shape padding={['s8', 's40']}>
        <Flex gap="s32" padding={['s0', 's32']} align="start">
          <Flex align="center" gap="s16">
            {displayConditionItems.map((conditionItem, index) => {
              const { product, requiredQuantity, key } = conditionItem;
              const isLastItem = !!residualProducts.length
                ? false
                : index === displayConditionItems.length - 1;

              return (
                <>
                  <ConditionItem
                    key={`${index}${conditionItem.key}`}
                    conditionKey={key}
                    product={product}
                    requiredQuantity={requiredQuantity}
                    onClickProductChange={onClickProductChange}
                    tracking={getTrackingData({
                      isTrackContent: true,
                      regionName: 'productComboList',
                      contentName: 'btnChangeProductCombo',
                      payload: JSON.stringify({
                        comboTab: `Combo ${totalProduct} sản phẩm`,
                        promotionType: 'combo',
                        skuIndex: index + 1,
                        sku: product.sku,
                      }),
                    })}
                  />
                  {!isLastItem ? (
                    <Flex
                      width="40px"
                      height="40px"
                      backgroundColor="background"
                      align="center"
                      justify="center"
                      style={{
                        borderRadius: '999px',
                        fontSize: '20px',
                      }}
                    >
                      +
                    </Flex>
                  ) : null}
                </>
              );
            })}
            {!!residualProducts.length && (
              <CompactedConditionItem
                residualProducts={residualProducts}
                onClickProductChange={onClickProductChange}
              />
            )}
          </Flex>
          {!!promotion.benefit ? (
            <PromotionBenefitWrapper paddingLeft="s32">
              <PromotionBenefitInfo promotionBenefit={promotion.benefit} />
            </PromotionBenefitWrapper>
          ) : null}
        </Flex>
      </Shape>
    </>
  );
};

const ConditionItem: React.FC<ConditionItemProps> = (props) => {
  const {
    product,
    requiredQuantity,
    conditionPromotion,
    tracking,
    conditionKey,
    onClickProductChange,
  } = props;
  const { imageUrl, name, price, sku } = product;
  const { numberDisplayFormat, applyNewLogicToCalculateComboPrice } =
    useTenantConfig();
  const latestPrice =
    applyNewLogicToCalculateComboPrice ||
    !conditionPromotion?.condition?.exclusions?.length
      ? price.latestPrice
      : price.supplierRetailPrice;
  const link = {
    href: {
      pathname: `/products/${sku}`,
    },
  };
  return (
    <CustomLink {...link}>
      <Flex
        width="320px"
        height="104px"
        borderRadius="xl"
        border="1px solid divider"
        gap="s8"
        padding="s8"
        align="center"
      >
        <ImageWrapper>
          <LazyImage
            width="88px"
            height="88px"
            src={imageUrl}
            hover="zoom"
            contain
            aspectRatio={1}
            alt={name}
            borderRadius={4}
          />
          {requiredQuantity > 1 && (
            <QuantityTag backgroundColor="primary500">
              {requiredQuantity}x
            </QuantityTag>
          )}
        </ImageWrapper>
        <Flex
          direction="column"
          justify="space-between"
          height="100%"
          width="208px"
        >
          <Body level={1} line={2}>
            {name}
          </Body>
          <Flex
            direction="row"
            height="40px"
            align="end"
            justify="space-between"
            width="100%"
          >
            <ProductPrice
              latestPrice={latestPrice}
              supplierRetailPrice={
                price.supplierRetailPrice > latestPrice
                  ? price.supplierRetailPrice
                  : undefined
              }
              textColor="textPrimary"
              numberDisplayFormat={numberDisplayFormat}
            />
            {conditionKey > 1 && (
              <Button
                {...tracking}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  onClickProductChange(sku, conditionKey);
                }}
                variant="primary"
                style={{
                  height: '24px',
                  padding: '0px 8px',
                  borderRadius: '9999px',
                }}
              >
                {t('Thay đổi')}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </CustomLink>
  );
};

const CompactedConditionItem: React.FC<CompactedConditionItemProps> = (
  props,
) => {
  const { residualProducts, onClickProductChange } = props;
  return (
    <CompactedConditionItemWrapper
      width="104px"
      height="104px"
      padding="s8"
      border="1px solid background"
    >
      <Dropdown
        placement="left"
        menu={
          <Shape width="336px" style={{ zIndex: 9999999 }}>
            <Shape padding={['s8', 's16']} marginBottom="s8">
              <SubTitle>{t('Sản phẩm trong combo')}</SubTitle>
            </Shape>
            <Flex gap="s8" padding={['s0', 's8']} direction="column">
              {residualProducts.map((item, index) => (
                <ConditionItem
                  key={`${index}${item.key}`}
                  conditionKey={item.key}
                  product={item.product}
                  requiredQuantity={item.requiredQuantity}
                  tracking={item.tracking}
                  onClickProductChange={onClickProductChange}
                />
              ))}
            </Flex>
          </Shape>
        }
      >
        <RemainCoverWrapper>
          <LazyImage
            width="88px"
            height="88px"
            src={residualProducts[0].product.imageUrl}
            hover="zoom"
            contain
            aspectRatio={1}
            alt={residualProducts[0].product.name}
            borderRadius={4}
          />
        </RemainCoverWrapper>
        <StyledRemainBg>+{residualProducts.length}</StyledRemainBg>
      </Dropdown>
    </CompactedConditionItemWrapper>
  );
};

const QuantityTag = styled(Shape)`
  position: absolute;
  height: 16px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: white;
  padding: 2px 5px;
  top: 4px;
  left: 4px;
`;

const StyledRemainBg = styled.div`
  position: absolute;
  width: 88px;
  height: 88px;
  background-color: rgba(27, 29, 41, 0.5);
  color: #ffffff;
  z-index: 1;
  top: 6px;
  left: 6px;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
`;

const RemainCoverWrapper = styled(Shape)`
  position: absolute;
  width: 88px;
  height: 88px;
  top: 6px;
  left: 6px;
  opacity: 80%;
  background: #1b1d29;
  border-radius: 4px;
`;

const ImageWrapper = styled(Shape)`
  position: relative;
`;

const CompactedConditionItemWrapper = styled(Shape)`
  position: relative;
`;

const PromotionBenefitWrapper = styled(Shape)`
  position: relative;
  &:before {
    content: ' ';
    position: absolute;
    left: 0;
    top: 32px;
    border-right: 1px solid #e4e5f0;
    height: 40px;
  }
`;
